import type { ReactNode } from "react"
import { CheckCircle2, TriangleAlert } from "lucide-react"

import { formatAmount } from "@/lib/format"
import { cn } from "@/lib/utils"
import { t } from "@/lib/i18n"

interface BalanceStatusProps {
  balanced: boolean
  /** Debe − Haber. Solo se muestra cuando no cuadra. */
  difference?: number
  debit: number
  credit: number
  /** "al 31/07/2026" */
  asOf?: ReactNode
  className?: string
}

/**
 * Cuadre del balance.
 *
 * Un balance que no cuadra tiene que gritar — pero gritar en un idioma
 * contable, no con un banner rojo de marketing. Cuando no cuadra:
 * trama diagonal en la superficie, filete rojo, y la DIFERENCIA en cuerpo
 * grande, que es el unico numero que le importa a quien esta mirando.
 * Cuando cuadra: una linea sobria, casi invisible. La ausencia de alarma
 * es la informacion.
 */
export function BalanceStatus({
  balanced,
  difference = 0,
  debit,
  credit,
  asOf,
  className,
}: BalanceStatusProps) {
  if (balanced) {
    return (
      <div
        className={cn(
          "flex flex-wrap items-center gap-x-3 gap-y-1 rounded-md border border-success/25 bg-success-soft px-2.5 py-1.5",
          className,
        )}
      >
        <CheckCircle2 className="h-3.5 w-3.5 shrink-0 text-success" />
        <span className="text-xs font-medium text-success">
          {t("Balance cuadrado")}{asOf ? <span className="font-normal"> {asOf}</span> : null}
        </span>
        <dl className="ml-auto flex items-center gap-4 text-2xs">
          <Totals debit={debit} credit={credit} />
        </dl>
      </div>
    )
  }

  return (
    <div
      role="alert"
      className={cn(
        "hatch-alert animate-alert-in rounded-md border-2 border-destructive/70 bg-destructive-soft",
        className,
      )}
    >
      <div className="flex flex-wrap items-center gap-x-5 gap-y-2 p-2.5">
        <div className="flex items-start gap-2">
          <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
          <div>
            <p className="text-sm font-semibold uppercase tracking-[0.04em] text-destructive">
              {t("El balance no cuadra")}</p>
            <p className="text-2xs text-foreground/75">
              {asOf ? <>{t("Corte")}{" "}{asOf}. </> : null}
              {t("No se puede firmar un periodo en este estado: revisa los asientos del rango antes de seguir.")}</p>
          </div>
        </div>

        <div className="ml-auto flex items-end gap-5">
          <dl className="flex items-end gap-4 text-2xs">
            <Totals debit={debit} credit={credit} />
          </dl>
          <div className="border-l border-destructive/30 pl-5 text-right">
            <dt className="eyebrow text-destructive/80">{t("Diferencia")}</dt>
            <dd className="num text-2xl font-semibold leading-none text-destructive">
              {formatAmount(Math.abs(difference))}
            </dd>
          </div>
        </div>
      </div>
    </div>
  )
}

function Totals({ debit, credit }: { debit: number; credit: number }) {
  return (
    <>
      <div className="text-right">
        <dt className="eyebrow">{t("Debe")}</dt>
        <dd className="num text-xs font-semibold text-foreground">{formatAmount(debit)}</dd>
      </div>
      <div className="text-right">
        <dt className="eyebrow">{t("Haber")}</dt>
        <dd className="num text-xs font-semibold text-foreground">{formatAmount(credit)}</dd>
      </div>
    </>
  )
}
